import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { memo, useEffect, useMemo, useState } from "react";
import { useIntl } from "../../contexts/IntlContext";
import Input from "../ui/Input";
import LoadingButton from "../ui/LoadingButton";

export type DeletionPreviewRow = {
  label: string;
  value: number;
};

type Props = {
  open: boolean;
  title: string;
  description: string;
  entityName: string;
  loading: boolean;
  loadError?: string | null;
  rows: DeletionPreviewRow[];
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
};

const DeletionPreviewDialog = memo(function DeletionPreviewDialog({
  open,
  title,
  description,
  entityName,
  loading,
  loadError,
  rows,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onClose,
}: Props) {
  const intl = useIntl();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [typedName, setTypedName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setTypedName("");
  }, [open]);

  const isNameConfirmed = useMemo(
    () => typedName.trim().toLowerCase() === entityName.trim().toLowerCase(),
    [typedName, entityName]
  );

  async function handleConfirm() {
    setSubmitting(true);
    try {
      await onConfirm();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      aria-labelledby="deletion-preview-dialog-title"
      fullWidth={fullScreen}
      maxWidth="xs"
    >
      <DialogTitle
        id="deletion-preview-dialog-title"
        sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 }, pb: 1 }}
      >
        {title}
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: 0 }}>
        <Typography variant="body2" sx={{ wordBreak: "break-word", mb: 1.5 }}>
          {description}
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : loadError ? (
          <Alert severity="error" sx={{ mb: 1 }}>
            {loadError}
          </Alert>
        ) : (
          <List dense disablePadding sx={{ mb: 1 }}>
            {rows.map((row) => (
              <ListItem key={row.label} disableGutters sx={{ py: 0.25 }}>
                <ListItemText
                  primary={row.label}
                  slotProps={{
                    primary: { variant: "body2", color: "text.secondary" },
                  }}
                />
                <Typography variant="body2" fontWeight={600}>
                  {row.value}
                </Typography>
              </ListItem>
            ))}
          </List>
        )}

        {!loading && !loadError && (
          <>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {intl.formatMessage(
                { id: "deletionPreview.typeToConfirm" },
                { name: entityName }
              )}
            </Typography>
            <Input
              autoFocus
              size="small"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder={entityName}
              disabled={submitting}
            />
          </>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          pb: { xs: 2, sm: 3 },
          pt: 1,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <LoadingButton
          onClick={onClose}
          variant="text"
          fullWidth={false}
          disabled={submitting}
        >
          {cancelLabel || intl.formatMessage({ id: "common.cancel" })}
        </LoadingButton>
        <LoadingButton
          onClick={handleConfirm}
          variant="contained"
          color="error"
          fullWidth={false}
          loading={submitting}
          disabled={loading || !!loadError || !isNameConfirmed}
        >
          {confirmLabel || intl.formatMessage({ id: "common.confirm" })}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
});

export default DeletionPreviewDialog;
